/**
 * THE NUMBERS AN OWNER ACTUALLY RUNS THE SHOP ON.
 *
 * Anthony: "does the owner/admin have a dashboard option that shows a
 * reporting function, that will allow me for example to run performance
 * reports like rev/leads/conversion etc in tiles."
 *
 * Three rules this file keeps, because reporting is where software lies to
 * people most easily:
 *
 *   1. EVERY FIGURE IS DATED BY ITS OWN EVENT. Revenue is dated by when
 *      the invoice was issued, not when the job was created. A conversion
 *      rate compares things that happened in the same window. The railway
 *      already learned this lesson the hard way.
 *
 *   2. A RATE WITH A TINY DENOMINATOR IS NOT A RATE. Two leads and one
 *      booking is not "50% conversion", it is two leads. Anything under
 *      the floor comes back as null and the screen says so instead of
 *      printing a percentage nobody should act on.
 *
 *   3. MONEY COMES FROM INVOICES AND PAYMENTS, never from job status.
 *      A job marked paid whose invoice was never raised is a bookkeeping
 *      problem, and a revenue figure that quietly counts it hides the very
 *      thing an owner needs to see.
 *
 * A caution that belongs on the screen, not just in this comment: most of
 * the history here was imported, and the old system recorded cash taken at
 * the curb inconsistently. Older periods will understate what was actually
 * collected. Recent periods are trustworthy; a year-over-year comparison
 * on this data is not.
 */

import { db } from "./client.ts";
import { spanFrom, type Span } from "./funnel.ts";
import { IMPORT_WATERMARK } from "../domain/watermark.ts";

/** Below this many, a percentage is noise wearing a suit. */
export const RATE_FLOOR = 5;

export type Metric = {
  key: string;
  label: string;
  /** Already formatted — money, a count, or a percentage. */
  value: string;
  /** One line under it, saying what it is made of. */
  note: string;
  kind: "money" | "count" | "rate";
  /** Null when there was not enough to say anything honest. */
  known: boolean;
};

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const pct = (a: number, b: number) => `${Math.round((a / b) * 100)}%`;

/** count(*) with the window applied to whichever column dates that thing. */
async function countIn(
  table: string, build: (q: any) => any, dateOn: string, from: string | null,
): Promise<number> {
  const sb = await db();
  let q = sb.from(table).select("id", { count: "exact", head: true });
  if (from) q = q.gte(dateOn, from);
  const { count, error } = await build(q);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** Sum a money column. PostgREST has no SUM, so the column travels. */
async function sumIn(
  table: string, column: string, build: (q: any) => any, dateOn: string, from: string | null,
): Promise<number> {
  const sb = await db();
  let q = sb.from(table).select(column).limit(10000);
  if (from) q = q.gte(dateOn, from);
  const { data, error } = await build(q);
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<Record<string, unknown>>)
    .reduce((t, r) => t + Number(r[column] ?? 0), 0);
}

/**
 * QUOTES WE ACTUALLY SENT, AND WHAT BECAME OF THEM.
 *
 * Conversion is not "job rows that reached paid" and it is not "leads that
 * booked". The desk opens a job row for every enquiry, and duplicates happen,
 * so counting rows flatters the number; the old system's entire history was
 * imported already-approved, so it flatters it more; and the lead table is
 * three-quarters cancelled spam, so counting leads buries it at 3%. None of
 * those is the question an owner is asking.
 *
 * The honest denominator is a quote a customer was actually shown: a job that
 * has an `estimate_sent` event. Counted ONCE per job however many times it was
 * sent or however many duplicate rows exist, and dated by the send, so a month
 * means quotes sent that month. Training fixtures are left out. Imported jobs
 * never had a desk send, so they fall out on their own.
 *
 * "Became work" is a yes on that same quote — an approval or authorization
 * (portal or phone), or a job that has since left 'estimate' for scheduled or
 * paid. Both halves are the same population of sent quotes, so the rate
 * compares like with like.
 */
async function sentQuotes(from: string | null): Promise<{ sent: number; won: number; declined: number }> {
  const sb = await db();

  let s = sb.from("job_events").select("job_id").eq("kind", "estimate_sent").limit(10000);
  if (from) s = s.gte("created_at", from);
  const { data: sends, error: se } = await s;
  if (se) throw new Error(se.message);
  const sentIds = [...new Set(((sends ?? []) as Array<{ job_id: string | null }>)
    .map((r) => r.job_id).filter((x): x is string => Boolean(x)))];
  if (!sentIds.length) return { sent: 0, won: 0, declined: 0 };

  // Drop training fixtures; learn each sent job's current status.
  const { data: jrows, error: je } = await sb.from("jobs")
    .select("id,status,is_training").in("id", sentIds).limit(10000);
  if (je) throw new Error(je.message);
  const jobs = ((jrows ?? []) as Array<{ id: string; status: string; is_training: boolean | null }>)
    .filter((j) => j.is_training !== true);
  const real = new Set(jobs.map((j) => j.id));
  if (!real.size) return { sent: 0, won: 0, declined: 0 };

  const WON_STATUS = new Set(["scheduled", "in_progress", "completed", "invoiced", "paid"]);
  const won = new Set(jobs.filter((j) => WON_STATUS.has(j.status)).map((j) => j.id));
  const declined = new Set<string>();

  const { data: evs, error: ee } = await sb.from("job_events").select("job_id,kind")
    .in("job_id", [...real])
    .in("kind", ["quote_approved", "authorization", "authorized", "auth_received", "quote_declined"])
    .limit(10000);
  if (ee) throw new Error(ee.message);
  for (const e of (evs ?? []) as Array<{ job_id: string; kind: string }>) {
    if (e.kind === "quote_declined") declined.add(e.job_id);
    else won.add(e.job_id);
  }

  let turnedDown = 0;
  for (const id of declined) if (!won.has(id)) turnedDown++;
  return { sent: real.size, won: won.size, declined: turnedDown };
}

export async function report(span: Span): Promise<Metric[]> {
  const from = spanFrom(span);

  const [
    billed, collected, invoiceCount,
    completed, completedImported, leadsIn, leadsLost,
    quotes,
  ] = await Promise.all([
    // Issued, not "job says paid". Voids are excluded because a voided
    // invoice is the shop saying that sale did not happen.
    sumIn("invoices", "total", (q: any) => q.neq("status", "void"), "issued_at", from),
    sumIn("payments", "amount", (q: any) => q, "received_at", from),
    countIn("invoices", (q: any) => q.neq("status", "void"), "issued_at", from),

    // FINISHED IS THE STAMP, NOT THE WORD. Only ten jobs ever reach the
    // literal status "completed" -- the terminal state in this shop is
    // "paid", because money closes a job. Asking for one status word
    // reported ten finished jobs for every window from a month to all
    // time, while 1,651 sat in "paid". Every finishing state stamps
    // completed_at, so that is what gets counted, and a state added later
    // is counted without anybody remembering to edit this line.
    countIn("jobs", (q: any) => q.not("completed_at", "is", null), "completed_at", from),

    // AND HOW MANY OF THOSE ARE THE MIGRATION TALKING. 554 jobs share a
    // completed_at of 23 August because that is when they were carried
    // over, not when they were worked. Counting them is right -- the shop
    // did the work -- but printing "700 finished this month" without
    // saying so is the same lie in a smaller hat.
    countIn("jobs", (q: any) => q.not("completed_at", "is", null)
      .lt("created_at", IMPORT_WATERMARK), "completed_at", from),

    countIn("enquiries", (q: any) => q.eq("kind", "lead"), "created_at", from),
    countIn("enquiries", (q: any) => q.eq("kind", "lead").eq("status", "lost"), "created_at", from),

    // Conversion and volume both come from quotes actually SENT, not job rows
    // (imports + duplicates) and not leads (spam). See sentQuotes().
    sentQuotes(from),
  ]);

  /**
   * AVERAGE TICKET BALANCES AGAINST JOBS, not against invoices. Billed over
   * the number of invoices answered a different question -- the average size
   * of a bill -- and it did not reconcile with the job count on the same
   * screen, because imported jobs were carried in already paid with no invoice
   * row: 1,072 invoices sit under 1,684 finished jobs, so billed / invoices
   * read ~$505 while billed / jobs read ~$321. The owner reads these tiles
   * together, so the ticket is billed over jobs finished: multiply it by the
   * jobs and you get back the billing.
   */
  const ticket = completed ? billed / completed : 0;

  return [
    { key: "billed", kind: "money", known: true,
      label: "Billed", value: money(billed),
      note: `${invoiceCount} invoice${invoiceCount === 1 ? "" : "s"} issued` },

    { key: "collected", kind: "money", known: true,
      label: "Collected", value: money(collected),
      note: billed > collected
        ? `${money(billed - collected)} of it not recorded as paid`
        : "Everything issued is accounted for" },

    { key: "ticket", kind: "money", known: completed >= RATE_FLOOR,
      label: "Average ticket", value: completed >= RATE_FLOOR ? money(ticket) : "—",
      note: completed >= RATE_FLOOR
        ? `billed across ${completed} job${completed === 1 ? "" : "s"} finished`
        : `only ${completed} job${completed === 1 ? "" : "s"} finished — too few to average` },

    { key: "completed", kind: "count", known: true,
      label: "Jobs finished", value: String(completed),
      note: completedImported
        ? `${completed - completedImported} finished here, ` +
          `${completedImported} carried over from the old system`
        : "work that reached a finished state in this window" },

    { key: "leads", kind: "count", known: true,
      label: "Leads in", value: String(leadsIn),
      note: leadsLost ? `${leadsLost} of them closed as lost` : "none written off yet" },

    /* CONVERSION IS SCOPED TO QUOTES THE DESK ACTUALLY SENT, both halves, so
       the numerator can never exceed it -- the 'more jobs than quotes' the
       owner rightly flagged came from a standalone send-count tile sitting
       next to a finished-jobs count built from a different population (native
       sends vs imported history). That tile is gone. The honest caveat stays
       on the rate: a quote that loses usually just goes quiet, and the desk
       records few of those, so the real rate runs a little below this. */
    { key: "conversion", kind: "rate", known: quotes.sent >= RATE_FLOOR,
      label: "Conversion", value: quotes.sent >= RATE_FLOOR ? pct(quotes.won, quotes.sent) : "—",
      note: quotes.sent >= RATE_FLOOR
        ? `${quotes.won} of ${quotes.sent} quotes we sent got a yes — losses aren't all logged, so the true rate runs a little lower`
        : `only ${quotes.sent} quote${quotes.sent === 1 ? "" : "s"} sent — not enough to rate` },

    { key: "outstanding", kind: "money", known: true,
      label: "Still owed", value: money(Math.max(0, billed - collected)),
      note: "issued in this window and not yet settled" },
  ];
}

/**
 * WHO DID THE WORK.
 *
 * Counted from the join table rather than from `jobs.assigned_to`, because
 * a job can carry more than one technician and the assignment column only
 * ever holds one. A leaderboard that silently drops the second person on a
 * two-person job is worse than no leaderboard.
 */
export type TechLine = { name: string; jobs: number };

export async function byTechnician(span: Span, limit = 8): Promise<TechLine[]> {
  const sb = await db();
  const from = spanFrom(span);

  let q = sb.from("jobs").select("id,technicians:job_technicians(technician_id)")
    .eq("status", "completed").limit(5000);
  if (from) q = q.gte("completed_at", from);
  const { data, error } = await q;
  if (error) throw new Error(error.message);

  const tally = new Map<string, number>();
  for (const j of (data ?? []) as Array<{ technicians: Array<{ technician_id: string }> }>) {
    for (const t of j.technicians ?? []) {
      tally.set(t.technician_id, (tally.get(t.technician_id) ?? 0) + 1);
    }
  }
  if (!tally.size) return [];

  const { data: people } = await sb.from("technicians")
    .select("id,name").in("id", [...tally.keys()]);
  const nameOf = new Map(((people ?? []) as Array<{ id: string; name: string }>)
    .map((p) => [p.id, p.name]));

  return [...tally.entries()]
    .map(([id, jobs]) => ({ name: nameOf.get(id) ?? "Unknown", jobs }))
    .sort((a, b) => b.jobs - a.jobs)
    .slice(0, limit);
}

/**
 * PRE-PURCHASE INSPECTIONS, BY WHO TOOK THEM AND FROM WHICH DESK.
 *
 * Anthony, 2026-10-07: tag who did the work, on either dashboard. A PPI is
 * claimed by the first person to act on it, here or on the MechanicRank
 * desk through the bridge, and the claim row says which (src/data/ppi.ts,
 * server/desk/ppi.ts). This counts the last claim on each job in the span,
 * so a takeover credits whoever has it now. The same question as SQL is in
 * docs/ppi-prepay.md.
 */
export type PpiLine = { name: string; via: string; jobs: number };

export async function ppiByStaff(span: Span, limit = 12): Promise<PpiLine[]> {
  const sb = await db();
  const from = spanFrom(span);
  let q = sb.from("job_events").select("job_id,actor_id,meta,created_at")
    .in("kind", ["claimed", "reassigned"]).order("created_at", { ascending: true }).limit(5000);
  if (from) q = q.gte("created_at", from);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const owner = new Map<string, { actor: string; via: string }>();
  for (const e of (data ?? []) as Array<{ job_id: string; actor_id: string | null; meta: Record<string, unknown> | null }>) {
    if (e.actor_id) owner.set(e.job_id, { actor: e.actor_id, via: String(e.meta?.["via"] ?? "shop-desk") });
  }
  if (!owner.size) return [];
  const tally = new Map<string, { actor: string; via: string; jobs: number }>();
  for (const o of owner.values()) {
    const k = `${o.actor}|${o.via}`;
    const t = tally.get(k) ?? { ...o, jobs: 0 };
    t.jobs++;
    tally.set(k, t);
  }
  const { data: people } = await sb.from("profiles").select("id,full_name")
    .in("id", [...new Set([...tally.values()].map((t) => t.actor))]);
  const nameOf = new Map(((people ?? []) as Array<{ id: string; full_name: string }>).map((p) => [p.id, p.full_name]));
  return [...tally.values()]
    .map((t) => ({ name: nameOf.get(t.actor) ?? "Unknown", via: t.via, jobs: t.jobs }))
    .sort((a, b) => b.jobs - a.jobs)
    .slice(0, limit);
}
